import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Box, Button, Chip, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import CancelIcon from "@mui/icons-material/Cancel";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { addAuditAccount, getAudit, rescanAudit, unwrapGet, unwrapPost, updateAuditAccount } from "../../../../helpers/auditHub_helper";
import { AUDIT_STATUSES, MAX_ACCOUNT_REGIONS } from "../constants";
import { AWS_REGION_CODES, frameworkLabel, scorePercent } from "../utils";
import { AH_ACCENT, AH_ACCENT_HOVER, AH_BORDER, AH_MUTED, AH_TEXT, AH_TRACK, AH_WHITE, outlinedButtonSx } from "../theme";
import AddAccountDialog from "./AddAccountDialog";
import AddFrameworksDialog from "./AddFrameworksDialog";
import RemoveScopeDialog from "./RemoveScopeDialog";

/** There is no scan-status route; scores move when `latestRunId` reaches the triggered run. */
const SCAN_POLL_MS = 20000;
const SCAN_POLL_LIMIT_MS = 30 * 60 * 1000;

const LABEL_COLOR = "#334155";

const ACTION_BTN_SX = {
  width: 30,
  height: 30,
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  backgroundColor: "#f8fafc",
  color: "#64748b",
  padding: 0,
  flexShrink: 0,
  "&:hover": { backgroundColor: "#f1f5f9", color: "#334155" },
  "&.Mui-disabled": { color: "#cbd5e1", borderColor: "#f1f5f9", backgroundColor: "#f8fafc" },
};

const DELETE_BTN_SX = {
  ...ACTION_BTN_SX,
  color: "#dc2626",
  borderColor: "#fecaca",
  backgroundColor: "#fef2f2",
  "&:hover": { backgroundColor: "#fee2e2", color: "#991b1b" },
};

const headerButtonSx = {
  ...outlinedButtonSx,
  height: 32,
  minHeight: 32,
  fontSize: "0.8125rem",
  color: AH_ACCENT,
  borderColor: AH_ACCENT,
  "&:hover": {
    color: AH_ACCENT_HOVER,
    borderColor: AH_ACCENT_HOVER,
    backgroundColor: "#fff4eb",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    color: AH_ACCENT,
    borderColor: AH_ACCENT,
    opacity: 0.5,
  },
};

const scopeChipSx = {
  height: 26,
  maxWidth: "100%",
  borderRadius: "6px",
  backgroundColor: "#fff4eb",
  border: "1px solid #fed7aa",
  color: "#9a3412",
  fontSize: "0.8125rem",
  fontWeight: 500,
  "& .MuiChip-label": { px: 1, overflow: "hidden", textOverflow: "ellipsis" },
  "& .MuiChip-deleteIcon": {
    color: "#9a3412",
    fontSize: 16,
    "&:hover": { color: "#dc2626" },
  },
};

const regionChipSx = {
  height: 22,
  maxWidth: "100%",
  borderRadius: "6px",
  backgroundColor: AH_TRACK,
  border: `1px solid ${AH_BORDER}`,
  color: AH_TEXT,
  fontSize: "0.75rem",
  fontWeight: 500,
  "& .MuiChip-label": { px: 0.75, overflow: "hidden", textOverflow: "ellipsis" },
};

const OFFERED_REGIONS = new Set(AWS_REGION_CODES);

/** `regions` is the selected region codes. An empty list is every region, matching create and add account. */
const regionCodes = (value) => {
  const list = Array.isArray(value) ? value : [];
  return list
    .map((region) => (typeof region === "string" ? region.trim() : ""))
    .filter((code) => OFFERED_REGIONS.has(code));
};

const sameRegionSet = (left, right) => {
  if (left.length !== right.length) return false;
  const known = new Set(left);
  return right.every((code) => known.has(code));
};

const pickChipSx = (selected) => ({
  height: 28,
  borderRadius: "8px",
  fontSize: 12,
  fontWeight: 600,
  border: `1px solid ${selected ? AH_ACCENT : AH_BORDER}`,
  backgroundColor: selected ? "#fff4eb" : AH_WHITE,
  color: AH_TEXT,
  "&:hover": { backgroundColor: selected ? "#fff4eb" : AH_TRACK },
});

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

const accountLabelOf = (account) => account?.accountName || account?.accountNumber || "Account";

const scopeTooltip = (scope) => {
  const percent = scope.hasScanResult ? scorePercent(scope.auditScore) : null;
  const score = percent == null ? "Not yet scored" : `Score ${Math.round(percent)}%`;
  const total = Number(scope.progress?.total);
  const decided = Number(scope.progress?.decided) || 0;
  return Number.isFinite(total) && total > 0 ? `${score} · ${decided} of ${total} manual controls decided` : score;
};

const pendingAccount = (account, body, frameworks) => ({
  pending: true,
  auditAccountId: `pending:${body.accountNumber}`,
  accountNumber: body.accountNumber,
  accountName: account?.accountName || account?.accountCode || "",
  regions: Array.isArray(body.regions) ? body.regions : [],
  scopes: (Array.isArray(frameworks) ? frameworks : []).map((framework) => ({
    auditScopeId: `pending-scope:${body.accountNumber}:${framework?.complianceAddonId}`,
    framework,
    pending: true,
  })),
  body,
});

/** Messages go to `onNotice` as `{ severity, message }`, or `null` to clear; the dialog shows them in its footer. */
const AuditScopeSection = forwardRef(({ auditId, auditStatus, initialAccounts, disabled, onChanged, onNotice, onPendingChange }, ref) => {
  const [accounts, setAccounts] = useState(Array.isArray(initialAccounts) ? initialAccounts : []);
  const [pending, setPending] = useState([]);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const onNoticeRef = useRef(onNotice);
  onNoticeRef.current = onNotice;
  const setNotice = useCallback((notice) => {
    if (onNoticeRef.current) onNoticeRef.current(notice);
  }, []);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [frameworkAccount, setFrameworkAccount] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [regionDraft, setRegionDraft] = useState(null);
  const [rescanning, setRescanning] = useState(null);
  const [scanning, setScanning] = useState({});
  const scanStartedAt = useRef(0);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const editable = auditStatus === AUDIT_STATUSES.DRAFT;
  const canRescan = auditStatus === AUDIT_STATUSES.ACTIVE;
  const busy = disabled || Boolean(rescanning) || Boolean(regionDraft?.saving);

  useEffect(() => {
    setAccounts(Array.isArray(initialAccounts) ? initialAccounts : []);
  }, [initialAccounts]);

  useEffect(() => {
    setPending([]);
  }, [auditId]);

  useEffect(() => {
    if (onPendingChange) onPendingChange(pending.length > 0);
  }, [pending.length, onPendingChange]);

  const savedNumbers = useMemo(() => new Set(accounts.map((account) => account.accountNumber)), [accounts]);
  const visibleAccounts = useMemo(
    () => [...accounts, ...pending.filter((account) => !savedNumbers.has(account.accountNumber))],
    [accounts, pending, savedNumbers]
  );

  /** An audit covers one framework; a new account joins on it rather than picking its own. */
  const auditFrameworks = useMemo(() => {
    const byId = new Map();
    visibleAccounts.forEach((account) => {
      (Array.isArray(account.scopes) ? account.scopes : []).forEach((scope) => {
        const id = Number(scope.framework?.complianceAddonId);
        if (Number.isInteger(id) && !byId.has(id)) byId.set(id, scope.framework);
      });
    });
    return [...byId.values()];
  }, [visibleAccounts]);

  const reload = useCallback(async () => {
    try {
      const detail = unwrapGet(await getAudit(auditId));
      setAccounts(Array.isArray(detail.accounts) ? detail.accounts : []);
    } catch (err) {
      setNotice({ severity: "error", message: err?.message || "Unable to refresh this assessment's scope." });
    }
  }, [auditId, setNotice]);

  useEffect(() => {
    const pending = Object.entries(scanning);
    if (!pending.length) return undefined;
    let active = true;
    const timer = setInterval(async () => {
      if (Date.now() - scanStartedAt.current > SCAN_POLL_LIMIT_MS) {
        setScanning({});
        return;
      }
      try {
        const detail = unwrapGet(await getAudit(auditId));
        if (!active) return;
        const list = Array.isArray(detail.accounts) ? detail.accounts : [];
        setAccounts(list);
        const finished = pending
          .filter(([accountId, runId]) => {
            const account = list.find((item) => item.auditAccountId === accountId);
            const scopes = Array.isArray(account?.scopes) ? account.scopes : [];
            return !scopes.length || scopes.some((scope) => scope.latestRunId === runId);
          })
          .map(([accountId]) => accountId);
        if (finished.length) {
          setScanning((prev) => {
            const next = { ...prev };
            finished.forEach((accountId) => delete next[accountId]);
            return next;
          });
          if (onChangedRef.current) onChangedRef.current();
        }
      } catch (_error) {
        // The next tick retries; a failed poll must not end the scan indicator.
      }
    }, SCAN_POLL_MS);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [scanning, auditId]);

  useImperativeHandle(
    ref,
    () => ({
      commitPending: async () => {
        const queue = pendingRef.current;
        if (!queue.length) return;
        const posted = [];
        for (const item of queue) {
          try {
            unwrapPost(await addAuditAccount(auditId, item.body));
            posted.push(item.accountNumber);
          } catch (err) {
            const remaining = queue.filter((entry) => !posted.includes(entry.accountNumber));
            setPending(remaining);
            if (posted.length) await reload();
            throw err;
          }
        }
        setPending([]);
        await reload();
      },
    }),
    [auditId, reload]
  );

  const afterChange = (message) => {
    setNotice({ severity: "success", message });
    reload();
    if (onChanged) onChanged();
  };

  const startRegionEdit = (account) => {
    setRegionDraft({
      auditAccountId: account.auditAccountId,
      regions: regionCodes(account.regions),
      saving: false,
      error: "",
    });
  };

  const toggleDraftRegion = (code) => {
    setRegionDraft((prev) => {
      if (!prev || prev.saving) return prev;
      if (code == null) return { ...prev, regions: [], error: "" };
      const selected = prev.regions.includes(code);
      if (selected) return { ...prev, regions: prev.regions.filter((region) => region !== code), error: "" };
      if (prev.regions.length >= MAX_ACCOUNT_REGIONS) return prev;
      return { ...prev, regions: [...prev.regions, code], error: "" };
    });
  };

  const saveRegions = async (account) => {
    if (!regionDraft || regionDraft.saving || regionDraft.auditAccountId !== account.auditAccountId) return;
    const next = regionDraft.regions;
    if (sameRegionSet(next, regionCodes(account.regions))) {
      setRegionDraft(null);
      return;
    }
    if (account.pending) {
      setPending((prev) =>
        prev.map((item) => {
          if (item.accountNumber !== account.accountNumber) return item;
          const body = { ...item.body };
          if (next.length) body.regions = next;
          else delete body.regions;
          return { ...item, regions: next, body };
        })
      );
      setRegionDraft(null);
      setNotice({ severity: "success", message: `Updated regions for ${accountLabelOf(account)}.` });
      return;
    }
    setRegionDraft((prev) => (prev ? { ...prev, saving: true, error: "" } : prev));
    try {
      const detail = unwrapPost(await updateAuditAccount(auditId, account.auditAccountId, { regions: next }));
      if (Array.isArray(detail?.accounts)) setAccounts(detail.accounts);
      else await reload();
      setRegionDraft(null);
      setNotice({ severity: "success", message: `Updated regions for ${accountLabelOf(account)}.` });
      if (onChangedRef.current) onChangedRef.current();
    } catch (err) {
      setRegionDraft((prev) =>
        prev ? { ...prev, saving: false, error: err?.message || "Unable to update regions." } : prev
      );
    }
  };

  const rescan = async (account) => {
    if (busy) return;
    setRescanning(account?.auditAccountId || "all");
    setNotice(null);
    try {
      const result = unwrapPost(await rescanAudit(auditId, account?.auditAccountId));
      const runs = Array.isArray(result?.runs) ? result.runs : [];
      const failures = Array.isArray(result?.dispatchFailures) ? result.dispatchFailures.filter(Boolean) : [];
      if (runs.length) {
        scanStartedAt.current = Date.now();
        setScanning((prev) => {
          const next = { ...prev };
          runs.forEach((run) => {
            if (run.auditAccountId && run.runId) next[run.auditAccountId] = run.runId;
          });
          return next;
        });
      }
      setNotice(
        failures.length
          ? { severity: "warning", message: `Scan dispatch: ${failures.join(" ")}` }
          : {
              severity: "success",
              message: `Scan started for ${plural(runs.length, "account")}. Scores update as each scan completes.`,
            }
      );
      if (onChanged) onChanged();
    } catch (err) {
      setNotice({ severity: "error", message: err?.message || "Unable to start the scan." });
    } finally {
      setRescanning(null);
    }
  };

  return (
    <Box sx={{ gridColumn: { sm: "1 / -1" }, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", mb: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: LABEL_COLOR }}>Scope</Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "#74788d", lineHeight: 1.45 }}>
            {editable
              ? "New accounts are added when you save. Other scope changes apply immediately."
              : "Accounts and frameworks can be changed only while the assessment is a draft."}
          </Typography>
        </Box>
        {editable || (canRescan && accounts.length) ? (
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {editable ? (
              <Button
                variant="outlined"
                onClick={() => setAddAccountOpen(true)}
                disabled={busy}
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                sx={headerButtonSx}
              >
                Add Account
              </Button>
            ) : null}
            {canRescan && accounts.length ? (
              <Button
                variant="outlined"
                onClick={() => rescan(null)}
                disabled={busy}
                startIcon={
                  rescanning === "all" ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon sx={{ fontSize: 18 }} />
                }
                sx={headerButtonSx}
              >
                {rescanning === "all" ? "Starting" : "Rescan All"}
              </Button>
            ) : null}
          </Box>
        ) : null}
      </Box>

      {visibleAccounts.length === 0 ? (
        <Typography sx={{ fontSize: "0.8125rem", color: AH_MUTED, py: 1 }}>This assessment has no accounts.</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {visibleAccounts.map((account) => {
            const scopes = Array.isArray(account.scopes) ? account.scopes : [];
            const regions = regionCodes(account.regions);
            const label = accountLabelOf(account);
            const isScanning = Boolean(scanning[account.auditAccountId]);
            return (
              <Box
                key={account.auditAccountId || account.accountNumber}
                sx={{ border: `1px solid ${AH_BORDER}`, borderRadius: "8px", backgroundColor: AH_WHITE, px: 1.5, py: 1.25 }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                  <Box sx={{ minWidth: 0, flex: "1 1 200px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: AH_TEXT, overflowWrap: "anywhere" }}>
                        {label}
                      </Typography>
                      {account.pending ? (
                        <Box
                          component="span"
                          sx={{
                            px: 0.75,
                            height: 20,
                            borderRadius: "6px",
                            backgroundColor: AH_TRACK,
                            color: AH_MUTED,
                            fontSize: "0.6875rem",
                            fontWeight: 600,
                            lineHeight: "20px",
                          }}
                        >
                          Not saved
                        </Box>
                      ) : null}
                      {isScanning ? (
                        <Box
                          role="status"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 0.75,
                            height: 20,
                            borderRadius: "6px",
                            backgroundColor: "#fff4eb",
                            color: "#9a3412",
                            fontSize: "0.6875rem",
                            fontWeight: 600,
                          }}
                        >
                          <CircularProgress size={10} sx={{ color: AH_ACCENT }} />
                          Scanning
                        </Box>
                      ) : null}
                    </Box>
                    <Typography sx={{ fontSize: "0.75rem", color: AH_MUTED, mt: 0.25, overflowWrap: "anywhere" }}>
                      {account.accountNumber}
                    </Typography>
                    {regionDraft?.auditAccountId === account.auditAccountId ? (
                      <Box sx={{ mt: 0.75 }} aria-label={`Regions for ${label}`}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, maxHeight: 136, overflowY: "auto", pr: 0.5 }}>
                          <Chip
                            label="All Regions"
                            clickable
                            disabled={regionDraft.saving}
                            aria-pressed={regionDraft.regions.length === 0}
                            onClick={() => toggleDraftRegion(null)}
                            sx={pickChipSx(regionDraft.regions.length === 0)}
                          />
                          {AWS_REGION_CODES.map((code) => {
                            const selected = regionDraft.regions.includes(code);
                            return (
                              <Chip
                                key={code}
                                label={code}
                                clickable
                                disabled={regionDraft.saving || (!selected && regionDraft.regions.length >= MAX_ACCOUNT_REGIONS)}
                                aria-pressed={selected}
                                onClick={() => toggleDraftRegion(code)}
                                sx={pickChipSx(selected)}
                              />
                            );
                          })}
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={regionDraft.saving}
                            onClick={() => saveRegions(account)}
                            sx={{
                              height: 28,
                              minWidth: 72,
                              px: 1.25,
                              borderRadius: "6px",
                              textTransform: "none",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              boxShadow: "none",
                              backgroundColor: AH_ACCENT,
                              "&:hover": { backgroundColor: AH_ACCENT_HOVER, boxShadow: "none" },
                            }}
                          >
                            {regionDraft.saving ? "Saving" : "Save"}
                          </Button>
                          <Button
                            size="small"
                            disabled={regionDraft.saving}
                            onClick={() => setRegionDraft(null)}
                            sx={{ height: 28, minWidth: 72, px: 1.25, borderRadius: "6px", textTransform: "none", fontSize: "0.75rem", color: AH_MUTED }}
                          >
                            Cancel
                          </Button>
                          {regionDraft.error ? (
                            <Typography role="alert" sx={{ fontSize: "0.75rem", color: "#dc2626", overflowWrap: "anywhere" }}>
                              {regionDraft.error}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mt: 0.75 }} aria-label={`Regions for ${label}`}>
                        {regions.length ? (
                          regions.map((code) => <Chip key={code} size="small" label={code} sx={regionChipSx} />)
                        ) : (
                          <Chip size="small" label="All regions" sx={regionChipSx} />
                        )}
                      </Box>
                    )}
                  </Box>
                  {editable || canRescan ? (
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                      {canRescan ? (
                        <Tooltip title="Rescan Account">
                          <span>
                            <IconButton
                              size="small"
                              disabled={busy}
                              onClick={() => rescan(account)}
                              aria-label={`Rescan ${label}`}
                              sx={ACTION_BTN_SX}
                            >
                              {rescanning === account.auditAccountId ? (
                                <CircularProgress size={14} color="inherit" />
                              ) : (
                                <RefreshIcon sx={{ fontSize: 16 }} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                      {editable ? (
                        <Tooltip title="Edit regions">
                          <span>
                            <IconButton
                              size="small"
                              disabled={busy || regionDraft?.auditAccountId === account.auditAccountId}
                              onClick={() => startRegionEdit(account)}
                              aria-label={`Edit regions for ${label}`}
                              sx={ACTION_BTN_SX}
                            >
                              <EditOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                      {editable ? (
                        <Tooltip title="Remove Account">
                          <span>
                            <IconButton
                              size="small"
                              disabled={busy}
                              onClick={() => {
                                if (account.pending) {
                                  setPending((prev) => prev.filter((item) => item.accountNumber !== account.accountNumber));
                                  setRegionDraft((prev) => (prev?.auditAccountId === account.auditAccountId ? null : prev));
                                  setNotice({ severity: "success", message: `Removed ${label}.` });
                                  return;
                                }
                                setRemoveTarget({ account });
                              }}
                              aria-label={`Remove ${label}`}
                              sx={DELETE_BTN_SX}
                            >
                              <i className="mdi mdi-trash-can" style={{ fontSize: 15 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                    </Box>
                  ) : null}
                </Box>
                {scopes.length === 0 ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: "0.75rem", color: AH_MUTED }}>No framework on this account.</Typography>
                    {editable ? (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={busy}
                        onClick={() => setFrameworkAccount(account)}
                        sx={{ ...headerButtonSx, height: 26, minHeight: 26 }}
                      >
                        Add framework
                      </Button>
                    ) : null}
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                    {scopes.map((scope) => {
                      const name = frameworkLabel(scope.framework);
                      return (
                        <Tooltip key={scope.auditScopeId} title={scopeTooltip(scope)} placement="top">
                          <Chip
                            size="small"
                            label={name}
                            onDelete={editable && !busy && !account.pending ? () => setRemoveTarget({ account, scope }) : undefined}
                            deleteIcon={<CancelIcon aria-label={`Remove ${name} from ${label}`} />}
                            sx={scopeChipSx}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <AddAccountDialog
        auditId={auditId}
        existingAccountNumbers={visibleAccounts.map((account) => account.accountNumber)}
        frameworks={auditFrameworks}
        isLive={canRescan}
        stageOnly
        open={addAccountOpen}
        onClose={() => setAddAccountOpen(false)}
        onAdded={({ account, body, frameworks, result }) => {
          setAddAccountOpen(false);
          if (body) {
            const label = accountLabelOf(account);
            setPending((prev) =>
              prev.some((item) => item.accountNumber === body.accountNumber)
                ? prev
                : [...prev, pendingAccount(account, body, frameworks)]
            );
            setNotice({ severity: "success", message: `${label} will be added when you save.` });
            return;
          }
          const label = accountLabelOf(account);
          const runId = result?.scan?.runId;
          if (runId && result?.auditAccountId) {
            scanStartedAt.current = Date.now();
            setScanning((prev) => ({ ...prev, [result.auditAccountId]: runId }));
          }
          if (result?.scanDispatchFailed) {
            setNotice({
              severity: "warning",
              message: `Added ${label}, but its scan could not be started. Use Rescan Account to try again.`,
            });
            reload();
            if (onChanged) onChanged();
            return;
          }
          afterChange(runId ? `Added ${label}. Its scan has started.` : `Added ${label}.`);
        }}
      />
      <AddFrameworksDialog
        auditId={auditId}
        account={frameworkAccount}
        onlyAddonId={auditFrameworks[0]?.complianceAddonId}
        open={Boolean(frameworkAccount)}
        onClose={() => setFrameworkAccount(null)}
        onAdded={({ account }) => {
          setFrameworkAccount(null);
          afterChange(`Added the framework to ${accountLabelOf(account)}.`);
        }}
      />
      <RemoveScopeDialog
        auditId={auditId}
        target={removeTarget}
        open={Boolean(removeTarget)}
        onClose={() => setRemoveTarget(null)}
        onRemoved={({ account, scope, result }) => {
          setRemoveTarget(null);
          const removed = Number(result?.scopesRemoved) || 0;
          const base = scope
            ? `Removed ${frameworkLabel(scope.framework)} from ${accountLabelOf(account)}.`
            : `Removed ${accountLabelOf(account)}${removed ? ` and ${plural(removed, "framework")}` : ""} from this assessment.`;
          afterChange(base);
        }}
      />
    </Box>
  );
});

export default AuditScopeSection;
